import * as THREE from 'three';

/**
 * Converts taps and holds on the canvas into world-space points.
 * - short press → `onTapObject` (if it hit a pickable) or `onTap` (ground point)
 * - press held longer than HOLD_MS → `onHoldStart`, then `onHoldEnd(point, seconds)` on release
 */
const HOLD_MS = 220;

export class GroundPicker {
  onTap?: (p: THREE.Vector3) => void;
  /** objects that catch taps before the ground; the hit's `userData.tapName` is reported */
  pickables: THREE.Object3D[] = [];
  onTapObject?: (name: string, object: THREE.Object3D) => void;
  onHoldStart?: (p: THREE.Vector3) => void;
  onHoldMove?: (p: THREE.Vector3, seconds: number) => void;
  onHoldEnd?: (p: THREE.Vector3, seconds: number) => void;
  /** one finger moved more than a few pixels before the hold timer: camera orbit (screen px deltas) */
  onDrag?: (dx: number, dy: number) => void;
  /** two fingers: pinch scale factor per event and centre movement (screen px) */
  onPinch?: (scale: number, dx: number, dy: number) => void;
  /** the dog: a press that lands on her and then moves (or is held) is a stroke, not a camera orbit */
  petTarget: THREE.Object3D | null = null;
  onPetStart?: (p: THREE.Vector3) => void;
  /** `p` is the point on her under the finger, or null when the finger slides off her */
  onPetMove?: (p: THREE.Vector3 | null) => void;
  onPetEnd?: (seconds: number) => void;
  private petting = false;
  private onHer = false;
  private petHit = new THREE.Vector3();

  /** where the pointer is on the pet target, if anywhere */
  private hitHer(e: PointerEvent): boolean {
    if (!this.petTarget) return false;
    this.point(e);
    this.ray.setFromCamera(this.ndc, this.camera);
    const hits = this.ray.intersectObject(this.petTarget, true);
    if (!hits.length) return false;
    this.petHit.copy(hits[0].point);
    return true;
  }

  /** screen → NDC; with the mouse locked (first-person walk) every press aims through the centre */
  private point(e: PointerEvent): void {
    if (document.pointerLockElement === this.el) {
      this.ndc.set(0, 0);
      return;
    }
    const r = this.el.getBoundingClientRect();
    this.ndc.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
  }

  private ray = new THREE.Raycaster();
  private plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  private ndc = new THREE.Vector2();
  private hit = new THREE.Vector3();
  private downAt = 0;
  private holdTimer = 0;
  private holding = false;
  private pointerId = -1;
  private last = new THREE.Vector3();
  private downX = 0;
  private downY = 0;
  private prevX = 0;
  private prevY = 0;
  private dragging = false;
  private pointers = new Map<number, { x: number; y: number }>();
  private pinchDist = 0;
  private pinchCx = 0;
  private pinchCy = 0;

  constructor(
    private el: HTMLElement,
    private camera: THREE.Camera,
  ) {
    el.addEventListener('pointerdown', (e) => this.down(e));
    el.addEventListener('pointermove', (e) => this.move(e));
    el.addEventListener('pointerup', (e) => this.up(e));
    el.addEventListener('pointercancel', () => this.cancel());
  }

  private ground(e: PointerEvent): boolean {
    this.point(e);
    this.ray.setFromCamera(this.ndc, this.camera);
    return this.ray.ray.intersectPlane(this.plane, this.hit) !== null;
  }

  private down(e: PointerEvent): void {
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    this.el.setPointerCapture(e.pointerId);
    if (this.pointers.size === 2) {
      // second finger: cancel any tap/hold/drag, start a pinch
      window.clearTimeout(this.holdTimer);
      this.holding = false;
      this.dragging = false;
      this.pointerId = -1;
      const [a, b] = [...this.pointers.values()];
      this.pinchDist = Math.hypot(a.x - b.x, a.y - b.y);
      this.pinchCx = (a.x + b.x) / 2;
      this.pinchCy = (a.y + b.y) / 2;
      return;
    }
    if (this.pointerId !== -1) return;
    this.pointerId = e.pointerId;
    this.downAt = performance.now();
    this.downX = this.prevX = e.clientX;
    this.downY = this.prevY = e.clientY;
    this.holding = false;
    this.dragging = false;
    this.petting = false;
    this.onHer = this.hitHer(e);
    if (this.ground(e)) this.last.copy(this.hit);
    this.holdTimer = window.setTimeout(() => {
      if (this.dragging) return;
      if (this.onHer) {
        // a held finger on her: a scratch
        this.petting = true;
        this.onPetStart?.(this.petHit.clone());
        return;
      }
      this.holding = true;
      this.onHoldStart?.(this.last.clone());
    }, HOLD_MS);
  }

  private move(e: PointerEvent): void {
    const p = this.pointers.get(e.pointerId);
    if (p) {
      p.x = e.clientX;
      p.y = e.clientY;
    }
    if (this.pointers.size >= 2) {
      const [a, b] = [...this.pointers.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      const cx = (a.x + b.x) / 2;
      const cy = (a.y + b.y) / 2;
      if (this.pinchDist > 0) this.onPinch?.(d / this.pinchDist, cx - this.pinchCx, cy - this.pinchCy);
      this.pinchDist = d;
      this.pinchCx = cx;
      this.pinchCy = cy;
      return;
    }
    if (e.pointerId !== this.pointerId) return;
    if (this.onHer && !this.petting && !this.dragging && Math.hypot(e.clientX - this.downX, e.clientY - this.downY) > 6) {
      // the finger moved while on her: stroking
      window.clearTimeout(this.holdTimer);
      this.petting = true;
      this.onPetStart?.(this.petHit.clone());
    }
    if (this.petting) {
      this.onPetMove?.(this.hitHer(e) ? this.petHit.clone() : null);
      return;
    }
    if (!this.holding && !this.dragging && Math.hypot(e.clientX - this.downX, e.clientY - this.downY) > 12) {
      window.clearTimeout(this.holdTimer);
      this.dragging = true;
    }
    if (this.dragging) {
      this.onDrag?.(e.clientX - this.prevX, e.clientY - this.prevY);
      this.prevX = e.clientX;
      this.prevY = e.clientY;
      return;
    }
    if (this.ground(e)) this.last.copy(this.hit);
    if (this.holding) this.onHoldMove?.(this.last.clone(), (performance.now() - this.downAt) / 1000);
  }

  private up(e: PointerEvent): void {
    this.pointers.delete(e.pointerId);
    if (this.pointers.size === 1) this.pinchDist = 0;
    if (e.pointerId !== this.pointerId) return;
    window.clearTimeout(this.holdTimer);
    this.pointerId = -1;
    if (this.petting) {
      this.petting = false;
      this.onHer = false;
      this.onPetEnd?.((performance.now() - this.downAt) / 1000);
      return;
    }
    this.onHer = false;
    if (this.dragging) {
      this.dragging = false;
      return;
    }
    if (this.holding) {
      this.holding = false;
      if (this.ground(e)) this.last.copy(this.hit);
      this.onHoldEnd?.(this.last.clone(), (performance.now() - this.downAt) / 1000);
      return;
    }
    // short press: object first, then ground
    this.ground(e);
    this.ray.setFromCamera(this.ndc, this.camera);
    if (this.pickables.length) {
      const hits = this.ray.intersectObjects(this.pickables, true);
      if (hits.length) {
        let o: THREE.Object3D | null = hits[0].object;
        while (o && !o.userData.tapName) o = o.parent;
        if (o) {
          this.onTapObject?.(o.userData.tapName as string, hits[0].object);
          return;
        }
      }
    }
    if (this.ray.ray.intersectPlane(this.plane, this.hit)) this.onTap?.(this.hit.clone());
  }

  private cancel(): void {
    window.clearTimeout(this.holdTimer);
    this.pointerId = -1;
    this.holding = false;
    this.dragging = false;
    if (this.petting) this.onPetEnd?.((performance.now() - this.downAt) / 1000);
    this.petting = false;
    this.onHer = false;
    this.pointers.clear();
    this.pinchDist = 0;
  }
}
